"use client"

import { useCallback, useRef, useState, useEffect } from "react"
import {
  DETACHED_TAB_STORAGE_KEY,
  saveDetachedTabData,
  loadDetachedTabData,
  createTabSyncChannel,
  type DetachableTabData,
  type DetachableTabType,
} from "@/lib/detached-tab-sync"
import { openOrAddDetachedTab } from "@/lib/detached-window-utils"

export type { DetachableTabData, DetachableTabType }
export { DETACHED_TAB_STORAGE_KEY, createTabSyncChannel }

export function useDetachableTab(
  options: {
    onDetach?: (tab: DetachableTabData) => void
    threshold?: number
  } = {},
) {
  const { onDetach, threshold = 50 } = options
  const [isDragging, setIsDragging] = useState(false)
  const [isNearEdge, setIsNearEdge] = useState(false)
  const isNearEdgeRef = useRef(false)
  const dragDataRef = useRef<DetachableTabData | null>(null)

  const checkNearEdge = useCallback(
    (clientX: number, clientY: number) => {
      const nearLeft = clientX < threshold
      const nearRight = clientX > window.innerWidth - threshold
      const nearTop = clientY < threshold
      const nearBottom = clientY > window.innerHeight - threshold
      return nearLeft || nearRight || nearTop || nearBottom
    },
    [threshold],
  )

  const openDetachedWindow = useCallback((tab: DetachableTabData) => {
    return openOrAddDetachedTab(tab)
  }, [])

  const detachTab = useCallback(
    (tab: DetachableTabData) => {
      openDetachedWindow(tab)
      onDetach?.(tab)
    },
    [openDetachedWindow, onDetach],
  )

  const onDragStart = useCallback((e: React.DragEvent, tab: DetachableTabData) => {
    setIsDragging(true)
    dragDataRef.current = tab
    e.dataTransfer.setData("application/json", JSON.stringify(tab))
    e.dataTransfer.effectAllowed = "move"

    const dragImage = document.createElement("div")
    dragImage.className = "px-3 py-1.5 bg-blue-500 text-white text-sm rounded shadow-lg"
    dragImage.textContent = tab.name
    dragImage.style.position = "absolute"
    dragImage.style.top = "-1000px"
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 0, 0)
    setTimeout(() => document.body.removeChild(dragImage), 0)
  }, [])

  const onDrag = useCallback(
    (e: React.DragEvent) => {
      if (e.clientX === 0 && e.clientY === 0) return
      const nearEdge = checkNearEdge(e.clientX, e.clientY)
      isNearEdgeRef.current = nearEdge
      setIsNearEdge(nearEdge)
    },
    [checkNearEdge],
  )

  const onDragEnd = useCallback(
    (e: React.DragEvent) => {
      setIsDragging(false)
      const droppedOutside =
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight

      if ((droppedOutside || isNearEdgeRef.current) && dragDataRef.current) {
        detachTab(dragDataRef.current)
      }

      isNearEdgeRef.current = false
      setIsNearEdge(false)
      dragDataRef.current = null
    },
    [detachTab],
  )

  return {
    isDragging,
    isNearEdge,
    dragHandlers: { draggable: true, onDragStart, onDragEnd, onDrag },
    detachTab,
  }
}

export function useDetachedTabData(): DetachableTabData | null {
  const [tabData, setTabData] = useState<DetachableTabData | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get("id")
    if (id) {
      const data = loadDetachedTabData(id)
      if (data) {
        setTabData(data)
        return
      }
    }

    const type = params.get("type") as DetachableTabType | null
    if (type && id) {
      setTabData({ id, type, name: `Detached ${type}`, component: type })
    }
  }, [])

  return tabData
}
